import type { IncidentDomainResponse } from '@prerender/diagnostics-api';

import { LastMeasuredNote } from './CrawlerEvidence';
import { formatRequestCount } from './crawlerEvidenceFigures';
import DetailField from './DetailField';
import { EVIDENCE_TITLE } from './EvidenceTable';
import { formatDetectedAt } from './formatDetectedAt';
import { formatRate } from './formatMeasuredValue';
import {
  bandFor,
  bandRangeLabel,
  type BlockingFigures,
  formatMeasurementPeriod,
  isRecovered,
  measurementPeriodOf,
  type RateRules,
} from './originBlockingFigures';
import { severityLabel } from './severityLabels';
import SeverityTag from './SeverityTag';

type OriginBlockingDomainFiguresProps = {
  domain: IncidentDomainResponse;
  figures: BlockingFigures;
  rules: RateRules | null;
};

function bandTagLabel({ severity }: IncidentDomainResponse, rules: RateRules | null): string {
  const band = bandFor(rules, severity);

  return band === undefined ? severityLabel(severity) : `${severityLabel(severity)} · ${bandRangeLabel(band)}`;
}

function OriginBlockingDomainFigures({ domain, figures, rules }: OriginBlockingDomainFiguresProps) {
  const period = rules === null ? null : measurementPeriodOf(domain.lastDetectedAt, rules.windowDays);

  return (
    <section className="diagnostics-detail-section" aria-label={`${EVIDENCE_TITLE} for ${domain.domain}`}>
      <h2 className="diagnostics-detail-section-title">{EVIDENCE_TITLE}</h2>
      <dl className="diagnostics-domain-evidence-figures">
        {period !== null && (
          <DetailField name="Period" className="diagnostics-domain-evidence-period">
            {formatMeasurementPeriod(period)}
          </DetailField>
        )}
        <DetailField name="Crawler requests">{formatRequestCount(figures.totalRequests)}</DetailField>
        <DetailField name="Answered 403 / 503 / 504">{formatRequestCount(figures.blockedRequests)}</DetailField>
        <DetailField name="Blocked request rate">
          {formatRate(domain.metricValue)}
          <SeverityTag severity={domain.severity} label={bandTagLabel(domain, rules)} />
        </DetailField>
        <DetailField name="Affected URLs" className="diagnostics-domain-evidence-urls">
          {formatRequestCount(domain.affectedUrlCount)}
        </DetailField>
      </dl>
      {isRecovered(domain) && <LastMeasuredNote />}
      <dl className="diagnostics-domain-figures">
        <DetailField name="First detected">{formatDetectedAt(new Date(domain.firstDetectedAt))}</DetailField>
        <DetailField name="Last detected">{formatDetectedAt(new Date(domain.lastDetectedAt))}</DetailField>
      </dl>
    </section>
  );
}

export default OriginBlockingDomainFigures;
